import React, {PureComponent, lazy, Suspense} from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { change, touch } from 'redux-form'
import { pathOr, contains, isEmpty } from 'ramda'
import cn from 'classnames'
import { generate as generateId } from 'shortid'
import URLSearchParams from 'url-search-params'

import FilterButton from '../../components/Filters/Button'
import FilterWrapper from '../../components/Filters/Wrapper'
import FilterDates from '../../components/Filters/Dates'
import FilterRooms from '../../components/Filters/Rooms'
import FilterSort from '../../components/Filters/Sort'
import FilterCode from '../../components/Filters/Code'
import changeFilter from '../../actions/filters/filterChange'
import guestsChange from '../../actions/filters/guestsChange'
import applyFilter from '../../actions/filters/filterApply'
import clearFilter from '../../actions/filters/filterClear'
import roomAdd from '../../actions/filters/addRoom'
import removeRoom from '../../actions/filters/removeRoom'
import changePrice from '../../actions/filters/priceChange'
import changeAmenity from '../../actions/filters/amenityChange'
import changeCode from '../../actions/filters/codeChange'
import changeHotelType from '../../actions/filters/hotelTypeChange'
import sortChange from '../../actions/filters/sortChange';
import filterBackup from '../../actions/filters/filterBackup';
import filterApplyBackup from '../../actions/filters/filterApplyBackup';
import syncFilters from '../../../shared/actions/syncFilters';

import {
  ACTIVE_NONE,
  FILTER_DATES,
  FILTER_MORE,
  FILTER_ROOMS,
  FILTER_SORT,
  FILTER_CODE,
  FILTER_MORE_MIN_HOTELS_COUNT,
  SEARCH_PARAM_CONTRACT_NUMBER,
  HOMEPAGE_VIEW,
  SEARCH_FILTERS_FORM,
} from '../../constants'
import { PAGE_BF_FIRST_STEP, PAGE_HOTEL } from '../../../../constants'
import {
  countRooms,
  isMoreFiltersFieldsFilled,
  isCodeFiltersFieldFilled,
  selectedCode,
  selectedPeriod,
  selectedRooms,
  selectedSort,
  nightsFromPeriod,
  updateDatesInSearchString,
} from '../../utils'
import {
  pushFiltersOpen, pushFiltersClose, pushDatesSkip, pushSearchBarInteract, pushSearchNavigationClick
} from '../../../../helpers/gtm'
import { coreValidators } from '../../../shared/FormControls/CoreValidators'
import { linksToFirstStep, linkToFirstStep, pageView } from '../../helpers/drupalSettings'

import './filters.scss'

const FilterDetailed = lazy(() => import('../../components/Filters/FilterDetailed/FilterDetailedModal'))
const gtmLabelMap = {
  dates: 'date picker',
  rooms: 'pax',
  // code: 'promo',
  more: 'more filters',
}

class Filters extends PureComponent {
  buttonsFromWidget = document.querySelectorAll('#hotel-block-pushes .open-filter, #prices .open-filter')

  codeValidate = pathOr([SEARCH_PARAM_CONTRACT_NUMBER], ['data', 'code', 'form', 'validate'], this.props)
    .map(item => coreValidators[item])

  static propTypes = {
    data: PropTypes.shape({
      locale: FilterDates.propTypes.locale,
      dates: FilterDates.propTypes.titles,
      rooms: FilterRooms.propTypes.titles,
      sort: FilterSort.propTypes.titles,
      code: FilterCode.propTypes.titles,
      submit: PropTypes.shape({
        url: PropTypes.string,
        reserveBtn: PropTypes.string,
        reserveBtnUnavailable: PropTypes.string,
        available: PropTypes.bool,
      }),
    }),
    types: PropTypes.arrayOf(PropTypes.string),
    activeByDefault: PropTypes.string,
    datesRange: PropTypes.arrayOf(PropTypes.any),
    dates: PropTypes.arrayOf(PropTypes.any),
    datesApply: PropTypes.bool.isRequired,
    rooms: PropTypes.arrayOf(PropTypes.any),
    roomsApply: PropTypes.bool.isRequired,
    code: PropTypes.objectOf(Object),
    moreApply: PropTypes.bool.isRequired,
    codeApply: PropTypes.bool,
    sort: FilterSort.propTypes.sort,
    sortApply: PropTypes.bool.isRequired,
    activeFilter: PropTypes.string.isRequired,
    hotelsCount: PropTypes.number.isRequired,
    browser: PropTypes.objectOf(Object).isRequired,
    bookingUrl:  PropTypes.string,

    filterChangeAction: PropTypes.func.isRequired,
    addRoomAction: PropTypes.func.isRequired,
    removeRoomAction: PropTypes.func.isRequired,
    guestsChangeAction: PropTypes.func.isRequired,
    priceChangeAction: PropTypes.func.isRequired,
    amenityChangeAction: PropTypes.func.isRequired,
    hotelTypeChangeAction: PropTypes.func.isRequired,
    filterApplyAction: PropTypes.func.isRequired,
    filterClearAction: PropTypes.func.isRequired,
    pageIndex: PropTypes.number.isRequired,
    values: PropTypes.objectOf(Object),
    codeChangeAction: PropTypes.func.isRequired,
    activeField: PropTypes.string,
    syncErrors: PropTypes.objectOf(PropTypes.string),
    submitFailed: PropTypes.bool,
    bookingUrlDefault: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.any),
  }

  static defaultProps = {
    data: PropTypes.shape({
      locale: 'en-GB',
      dates: {
        label: 'Dates',
        title: 'Choose your dates',
        from: 'From',
        to: 'to',
        apply: 'Apply your dates',
      },
      rooms: {
        label: 'Guests & Rooms',
        title: 'Travelers and apartments',
        whoAccompaniesYou: 'Who accompanies you?',
        adult: 'adult',
        adults: 'adults',
        adultAge: 'over 12 years',
        child: 'child',
        children: 'children',
        childAge: 'from 3 to 11 years',
        baby: 'baby',
        babies: 'babies',
        babyAge: 'less than 2 years',
        room: 'room',
        rooms: 'rooms',
        wantBookOneMoreRoom: 'I want to book one more room',
        showOneMoreRoom: 'Show one more room',
        apply: 'Apply Aparthotels',
        remove: 'remove',
      },
      more: {
        label: 'More Filters',
        title: 'Filters',
        clear: 'Clear',
        priceRange: {
          title: 'How far would you go?',
          lowPrice: 'Less than 90€',
          middlePrice: 'Between 90 and 150€',
          highPrice: 'More than 150€',
        },
        amenities: {
          title: 'What would you like?',
          plus: 'The +',
          equipment: 'equipment',
          showMore: 'Show more',
          showLess: 'Show less',
          icons: {
            disabledPeople: 'Access for disabled people',
            petsAllowed: 'Pets allowed',
            parking: 'Parking',
            pool: 'Pool',
            sportsHall: 'Sports Hall',
            meetingRoom: 'Meeting Room',
            airConditioner: 'Air Conditioner',
          },
        },
        hotelType: {
          title: 'Type of aparthotel',
          icons: {
            business: 'Business',
            family: 'Family',
          },
        },
        apply: 'Show Aparthotels',
      },
      code: {
        label: 'Professional Code',
        title: 'Code professionnel',
        apply: 'Voir mes tarifs négociés',
        form: {
          title: 'Professionnel ? Saisissez votre code',
          subTitle: 'Cette offre est non cumulable avec d\'autres promotions en cours',
          labels: {
            clientCode: 'Voyage d\'affaires',
            travelAgency: 'Agence de voyages',
            promoCode: 'Code promo',
          },
          placeholders: {
            clientCode: 'Code client',
            contractNumber: 'Numéro de contrat',
            travelAgency: 'Code IATA',
            promoCode: '',
          },
          validate: ['contractNumber'],
        },
      },
      sort: {
        label: 'Sort by',
        hotelsInCity: '%n hotels in %s',
        hotelsInCityMobile: '%n hotels',
        title: 'Sort by',
        items: [
          'From cheaper to more expensive',
          'From the most expensive to the least expensive',
          'From highest rated to least well noted',
        ],
        apply: 'Show Aparthotels',
        cancel: 'Cancel',
      },
      submit: {
        url: '#',
        reserveBtn: 'Book',
        reserveBtnUnavailable: 'See available dates',
        available: false,
      },
    }),
    types: [],
    activeByDefault: null,
    datesRange: null,
    dates: null,
    rooms: null,
    more: null,
    code: null,
    sort: null,
    values: null,
    activeField: null,
    syncErrors: null,
    submitFailed: false,
    codeApply: false,
    bookingUrl: '',
    bookingUrlDefault: '',
  }

  state = {
    month: null,
    link2FirstStep: null,
    isHomePage: pageView === HOMEPAGE_VIEW,
  }

  componentDidMount() {
    const {
      activeByDefault,
      filterChangeAction,
      types,
      resultsList,
    } = this.props

    if (contains('dates', types) && Array.from(this.buttonsFromWidget).length > 0) {
      Array.from(this.buttonsFromWidget).forEach((item) => {
        item.addEventListener('click', this.onClickButtonFromWidget)
      })
    }

    if (activeByDefault && !this.isFilterApplied(activeByDefault) && (resultsList !== null && resultsList.length > 0)) {
      filterChangeAction('activeFilter', activeByDefault)
      document.body.classList.add('no-scroll')
    }

    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('extend-search')) {
        const extendedSearchCounter = sessionStorage.getItem('extendedSearchCounter')
        if (extendedSearchCounter > 0) {
          sessionStorage.setItem('extendedSearchCounter', 0)
        } else {
          event.preventDefault()
          const search = document.querySelector('.header-search__search-text')
          search.click()
          sessionStorage.setItem('extendedSearchCounter', 1)
        }
      }
    })
  }

  onClickButtonFromWidget = (event) => {
    event.preventDefault()
    this.onButtonClickHandler('dates', event)
  }

  onButtonClickHandler = (type, event, isHeaderButton) => {
    const link2FirstStep = linksToFirstStep[event.target.getAttribute('data-first-step-link')]

    this.setState({ link2FirstStep: link2FirstStep || linkToFirstStep })

    const {
      filterChangeAction,
      activeFilter,
      dates,
      pageIndex,
      bookingUrl,
      bookingUrlDefault,
    } = this.props

    const {
      isHomePage,
    } = this.state

    if (isHeaderButton) {
      // logic on hotel page without page reload
      if (pageIndex === PAGE_HOTEL) {
        if (this.isFilterApplied(FILTER_DATES)) {
          const toRoomrates = bookingUrl.length > 1 ? bookingUrl : bookingUrlDefault
          pushSearchNavigationClick()
          window.location.href = toRoomrates
        } else {
          filterChangeAction('activeFilter', FILTER_DATES)
        }
      } else {
        const { url, available } = pathOr({}, ['data', 'submit'], this.props)

        if (this.isFilterApplied((FILTER_DATES) || available) && url) {
          window.location.href = updateDatesInSearchString(url, dates)
          return
        }
      }
    }

    if (isHeaderButton && pageIndex === PAGE_HOTEL) {
    } else {
      pushFiltersOpen(type)
      pushSearchBarInteract({
        label: gtmLabelMap[type],
        action: 'open',
      });
      filterChangeAction('activeFilter', activeFilter === type ? ACTIVE_NONE : type)
    }
  }

  onDateSubmit = (filterDates, apply, datesRange) => {
    const { pageIndex } = this.props
    if (nightsFromPeriod(datesRange) > 120 && pageIndex === PAGE_BF_FIRST_STEP) {
      window.location = pathOr(window.location.search, ['drupalSettings', 'search', 'api', 'longStayRedirectUrl'], window)
    }
    const { filterApplyAction } = this.props
    filterApplyAction(filterDates, apply)
  }

  onFilterCloseHandler = (e, type, eventType) => {
    const {
      filterChangeAction,
      filterBackupAction,
      filterApplyBackupAction,
      activeFilter,
    } = this.props
    if (e) {
      e.stopPropagation()
    }
    if (type) {
      if (eventType === 'skip' && type === 'dates') {
        pushDatesSkip()
      } else {
        pushFiltersClose(type)
      }
    }

    if (eventType === 'submit') {
      filterBackupAction(activeFilter)
    } else {
      filterApplyBackupAction(activeFilter)
    }

    this.setState({ link2FirstStep: null })
    filterChangeAction('activeFilter', ACTIVE_NONE)
    document.body.classList.remove('no-scroll', 'no-scroll-mobile')
    document.body.classList.add('page-node-type-hotels')
  }

  onFilterClearHandler = async (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    const { activeFilter, filterClearAction, filterBackupAction } = this.props
    // need to check promocode directly in drupal settings, to be sure code was applied
    // before send request to remove it
    // otherwise we just clear state from not submited code that user typed
    const promoCode = pathOr('', ['drupalSettings', 'businessCodeFilter', 'promoCode'], window)
    await filterClearAction(activeFilter, false, promoCode)
    filterBackupAction(activeFilter)
  }

  getSelectedFields = () => {
    const {
      activeFilter: active,
      datesRange,
      sort,
      data: {
        dates: { from, to },
        rooms: { title: defaultSelectedRoomsTitle },
        code: { title: defaultSelectedCodeTitle },
        sort: { items },
        locale,
      },
    } = this.props
    let selected = ''

    switch (active) {
      case FILTER_DATES:
        selected = selectedPeriod(datesRange, from, to, locale)
        break
      case FILTER_ROOMS:
        selected = this.roomsButtonLabel(defaultSelectedRoomsTitle)
        break
      case FILTER_CODE:
        selected = this.codeButtonLabel(defaultSelectedCodeTitle)
        break
      case FILTER_SORT:
        selected = selectedSort(sort, items)
        break
      default:
    }
    return selected
  }

  filterTitle = (fieldName) => {
    const { data, more } = this.props
    const { activeFilter } = this.props

    if (activeFilter === FILTER_CODE && fieldName === 'title') {
      return ''
    }
    return data[activeFilter][fieldName]
  }

  isActive = (type) => {
    const { activeFilter } = this.props
    return activeFilter === type
  }

  clearLabel = () => {
    const { activeFilter, data } = this.props
    let label

    switch (activeFilter) {
      case FILTER_MORE:
        label = pathOr('Clear', ['more', 'clear'])(data)
        break
      case FILTER_CODE:
        label = pathOr('Clear', ['code', 'form', 'erase_title'])(data)
        break
      default: {
        label = null
      }
    }
    return label
  }

  isFilterClearable = () => {
    const { activeFilter, more, code } = this.props
    switch (activeFilter) {
      case FILTER_MORE:
        return isMoreFiltersFieldsFilled(more)
      case FILTER_CODE:
        return isCodeFiltersFieldFilled(code)
      default:
        return false
    }
  }

  isFilterFilled = () => {
    const {
      activeFilter: active,
      datesApply,
      rooms,
      roomsApply,
      more,
      moreApply,
      sort,
      sortApply,
      values,
      code,
      dates: datesRange,
    } = this.props
    let filled = false

    switch (active) {
      case FILTER_DATES:
        filled = Array.isArray(datesRange)
            && datesRange.length === 2
            && datesRange[1]
            && !datesApply
        break
      case FILTER_ROOMS:
        filled = countRooms(rooms) > 0 && !roomsApply
        break
      case FILTER_MORE:
        filled = (isMoreFiltersFieldsFilled(more) && !moreApply) || !!values
        break
      case FILTER_CODE:
        filled = isCodeFiltersFieldFilled(code)
        break
      case FILTER_SORT:
        filled = sort !== null && !sortApply
        break
      default:
    }
    return filled
  }

  isFilterApplied = (type = null) => {
    const {
      activeFilter: active,
      datesRange,
      datesApply,
      rooms,
      roomsApply,
      more,
      moreApply,
      sort,
      sortApply,
      codeApply,
      code,
    } = this.props
    let applied = false

    switch (type || active) {
      case FILTER_DATES:
        applied = Array.isArray(datesRange) && datesRange.length === 2 && datesApply
        break
      case FILTER_ROOMS:
        applied = countRooms(rooms) > 0 && roomsApply
        break
      case FILTER_MORE:
        applied = isMoreFiltersFieldsFilled(more) && moreApply
        break
      case FILTER_CODE:
        applied = isCodeFiltersFieldFilled(code) && codeApply
        break
      case FILTER_SORT:
        applied = sort !== null && sortApply
        break
      default:
    }
    return applied
  }

  isFilterVisible = (type) => {
    const { hotelsCount, more } = this.props
    let isVisible = true

    if (type === FILTER_MORE) {
      isVisible = hotelsCount >= FILTER_MORE_MIN_HOTELS_COUNT || isMoreFiltersFieldsFilled(more)
    }

    return isVisible
  }

  buttonLabel = (type) => {
    let label = ''
    switch (type) {
      case FILTER_DATES:
        label = this.datesButtonLabel()
        break
      case FILTER_ROOMS:
        label = this.roomsButtonLabel()
        break
      case FILTER_CODE:
        label = this.codeButtonLabel()
        break
      default:
        return ''
    }
    return label
  }

  datesButtonLabel = () => {
    const { datesRange, data: { dates: { label } } } = this.props
    let periodLabel = null
    if (Array.isArray(datesRange)) {
      const { data: { dates: { to }, locale } } = this.props
      const from = ''
      periodLabel = selectedPeriod(datesRange, from, to, locale)
    }

    return ((this.isFilterApplied(FILTER_DATES) || this.isActive(FILTER_DATES))
     && periodLabel)
      ? periodLabel : label
  }

  roomsButtonLabel = (defaultParam) => {
    const {
      rooms,
      data: {
        rooms: roomsTitles,
        rooms: { label: defaultLabel },
      },
    } = this.props
    return selectedRooms(rooms, roomsTitles, defaultParam || defaultLabel)
  }

  codeButtonLabel = (defaultParam) => {
    const {
      data: {
        code: codeTitles,
        code: { label: defaultLabel },
      },
    } = this.props

    return selectedCode(codeTitles, defaultParam || defaultLabel)
  }

  handleChangeMonth = (month) => {
    this.setState({ month })
  }

  render() {
    const {
      data: {
        locale,
        dates: datesTitles,
        rooms: roomsTitles,
        more: moreTitles,
        sort: sortTitles,
        code: codeTitles,
        submit,
      },
      types,
      sortChangeAction,
      filterChangeAction,
      filterApplyAction,
      guestsChangeAction,
      datesRange,
      rooms,
      addRoomAction,
      removeRoomAction,
      more,
      priceChangeAction,
      amenityChangeAction,
      codeChangeAction,
      hotelTypeChangeAction,
      sort,
      activeFilter: active,
      pageIndex,
      activeField,
      code,
      syncErrors,
      submitFailed,
      additionalValues,
      syncFiltersAction,
    } = this.props

    const buttons = types
      .filter(t => {
        return ![FILTER_SORT, FILTER_MORE].includes(t)
      })
      .map(type => (
        {
          id: generateId(),
          type,
          label: this.buttonLabel(type),
          isApplied: this.isFilterApplied(type),
          isVisible: this.isFilterVisible(type),
        }
      ))

    const filterErrors = submitFailed ? syncErrors : {}

    const { month, link2FirstStep } = this.state

    return (
      <div className="search-filters container">
        <div className={cn('search-filters', (pageIndex === PAGE_BF_FIRST_STEP) && 'bf-first-step', 'scroll')}>
          {buttons.map(button => (
            <React.Fragment key={button.id}>
              {button.isVisible && (
              <FilterButton
                label={button.label}
                onClick={this.onButtonClickHandler}
                filterType={button.type}
                isActive={this.isActive(button.type)}
                isApplied={button.isApplied}
              />
              )}
            </React.Fragment>
          ))}
          {!this.isActive(ACTIVE_NONE) && (
          <div ref={this.setInner}>
            <FilterWrapper
              month={month}
              filterType={active}
              closeLink={datesTitles.closeLink}
              title={this.filterTitle('title')}
              onFilterClose={this.onFilterCloseHandler}
              applyTitle={this.filterTitle('apply')}
              isFilterFilled={this.isFilterFilled()}
              isFilterApplied={this.isFilterApplied()}
              filterApplyAction={filterApplyAction}
              isFilterClearable={this.isFilterClearable()}
              clearLabel={this.clearLabel()}
              onFilterClear={this.onFilterClearHandler}
              link2FirstStep={link2FirstStep}
              filterChangeAction={filterChangeAction}
              isNoTemplate={this.isActive(FILTER_MORE)}
            >
              {this.isActive(FILTER_DATES) && (
              <FilterDates
                locale={locale}
                titles={datesTitles}
                dates={datesRange}
                handleChangeMonth={this.handleChangeMonth}
                syncFiltersAction={syncFiltersAction}
                filterChangeAction={filterChangeAction}
                filterApplyAction={this.onDateSubmit}
                getSelectedFields={this.getSelectedFields}
              />
              )}
              {this.isActive(FILTER_ROOMS) && (
              <FilterRooms
                titles={roomsTitles}
                rooms={rooms}
                addRoomAction={addRoomAction}
                removeRoomAction={removeRoomAction}
                guestsChangeAction={guestsChangeAction}
                filterApplyAction={filterApplyAction}
              />
              )}
              {this.isActive(FILTER_CODE) && (
              <FilterCode
                titles={codeTitles}
                activeField={activeField}
                codeChange={codeChangeAction}
                filterApplyAction={filterApplyAction}
                values={code}
                validate={this.codeValidate}
                syncErrors={filterErrors}
              />
              )}
              {this.isActive(FILTER_SORT) && (
              <FilterSort
                titles={sortTitles}
                sort={sort}
                filterChangeAction={filterChangeAction}
                filterApplyAction={filterApplyAction}
              />
              )}
              {this.isActive(FILTER_MORE) && (
                <Suspense>
                  <FilterDetailed
                    titles={moreTitles}
                    sortTitles={sortTitles}
                    sort={sort}
                    more={more}
                    additionalValues={additionalValues}
                    priceChangeAction={priceChangeAction}
                    amenityChangeAction={amenityChangeAction}
                    hotelTypeChangeAction={hotelTypeChangeAction}
                    filterApplyAction={filterApplyAction}
                    sortChangeAction={sortChangeAction}
                    onFilterClose={this.onFilterCloseHandler}
                    onFilterClear={this.onFilterClearHandler}
                    title={this.filterTitle('title')}
                    applyTitle={this.filterTitle('apply')}
                    clearLabel={this.clearLabel()}
                  />
                </Suspense>
              )}
            </FilterWrapper>
          </div>
          )}
        </div>
        {pageIndex === PAGE_HOTEL && (
          <div className="submit-button-container">
            <FilterButton
              label={submit.reserveBtn}
              unavailableLabel={submit.reserveBtn}
              isAvailable={submit.reserveBtn}
              onClick={this.onButtonClickHandler}
              filterType="availableDates"
              isActive
              isApplied
              className="submit"
            />
          </div>
        )}
      </div>
    )
  }
}

const datesRange = (dates) => {
  const URLParams = new URLSearchParams(window.location.search)
  if (URLParams.get('emptyCalendar') === 'true') return null
  return dates
}

const hotelsCount = results => (Array.isArray(results) ? results.length : 0)

export default connect(({
  form: {
    searchFilters: {
      dates,
      datesApply,
      rooms,
      roomsApply,
      more,
      moreApply,
      sort,
      sortApply,
      activeFilter,
      values,
      active,
      code,
      codeApply,
      syncErrors,
      submitFailed,
      additionalValues,
    },
  },
  main: {
    results,
    pageIndex,
    bookingUrl,
    setting,
  },
  browser,
}) => ({
  additionalValues,
  datesRange: datesRange(dates),
  dates,
  datesApply,
  rooms,
  roomsApply,
  more,
  moreApply,
  sort,
  sortApply,
  activeFilter,
  hotelsCount: hotelsCount(results),
  pageIndex,
  values,
  code,
  codeApply,
  activeField: active,
  syncErrors,
  submitFailed,
  browser,
  bookingUrl,
  bookingUrlDefault:  pathOr('', ['widgets', 'filters', 'submit', 'url'], setting),
  resultsList:  pathOr(null, ['searchResults', 'results'], setting),
}),
{
  filterChangeAction: changeFilter,
  addRoomAction: roomAdd,
  removeRoomAction: removeRoom,
  guestsChangeAction: guestsChange,
  priceChangeAction: changePrice,
  sortChangeAction: sortChange,
  amenityChangeAction: changeAmenity,
  codeChangeAction: changeCode,
  hotelTypeChangeAction: changeHotelType,
  filterApplyAction: applyFilter,
  filterClearAction: clearFilter,
  filterBackupAction: filterBackup,
  filterApplyBackupAction: filterApplyBackup,
  syncFiltersAction: syncFilters,
})(Filters)
